import {
  EQUIPMENT_ADMISSION_STATUSES,
  EQUIPMENT_ADMISSION_SURFACES,
  EQUIPMENT_DOMAINS,
  EQUIPMENT_EVIDENCE_STATUSES,
  EQUIPMENT_KINDS,
  EQUIPMENT_LEVELS,
  EQUIPMENT_LIBRARY_DIGEST_ALGORITHM,
  EQUIPMENT_LIBRARY_SCHEMA_VERSION,
  EQUIPMENT_LIFECYCLE_STATUSES,
  EQUIPMENT_METRICS,
  EQUIPMENT_REVISION_STATUSES,
  EQUIPMENT_SOURCE_ACCESS,
  EQUIPMENT_SOURCE_KINDS,
  EQUIPMENT_TECHNOLOGIES,
  LEGACY_EQUIPMENT_NAMESPACES,
  type CompanyEquipmentPatch,
  type EquipmentAdmission,
  type EquipmentAdmissionSurface,
  type EquipmentDigestFields,
  type EquipmentLibraryBundle,
  type EquipmentLibraryProduct,
  type EquipmentMarketEvidence,
  type EquipmentObservation,
  type EquipmentObservedValue,
  type EquipmentPlanningBenchmark,
  type EquipmentRevision,
  type EquipmentSource,
  type LegacyEquipmentIdLink,
  type PlanningBenchmarkMetric,
} from '../../types/equipmentLibrary';
import {
  type EquipmentLibraryRecordKind,
  verifyEquipmentLibraryRecordDigest,
} from './digests';

export interface EquipmentLibraryValidationIssue {
  path: string;
  code:
    | 'required'
    | 'type'
    | 'enum'
    | 'format'
    | 'range'
    | 'duplicate'
    | 'reference'
    | 'integrity'
    | 'invariant';
  message: string;
}

export interface EquipmentLibraryValidationResult {
  valid: boolean;
  issues: readonly EquipmentLibraryValidationIssue[];
}

type MutableIssues = EquipmentLibraryValidationIssue[];
type UnknownRecord = Record<string, unknown>;

const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/;
const SHA256_PATTERN = /^[a-f\d]{64}$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ISO_INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/;

const OUTPUT_BOUNDARIES = [
  'engine-shaft',
  'generator-terminals',
  'package-gross',
  'package-net',
  'plant-gross',
  'plant-net',
  'grid-interconnection',
] as const;
const QUALIFIERS = ['minimum', 'maximum', 'nominal', 'typical', 'guaranteed', 'range'] as const;
const HEATING_VALUE_BASES = ['LHV', 'HHV', 'not-applicable', 'not-stated'] as const;
const LIFECYCLE_BOUNDARIES = [
  'direct-operation',
  'upstream-fuel',
  'construction',
  'decommissioning',
  'cradle-to-gate',
  'cradle-to-grave',
] as const;

function result(issues: MutableIssues): EquipmentLibraryValidationResult {
  return { valid: issues.length === 0, issues };
}

function issue(
  issues: MutableIssues,
  path: string,
  code: EquipmentLibraryValidationIssue['code'],
  message: string,
): void {
  issues.push({ path, code, message });
}

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function recordAt(
  value: unknown,
  path: string,
  issues: MutableIssues,
): UnknownRecord | null {
  if (!isRecord(value)) {
    issue(issues, path, 'type', 'must be an object');
    return null;
  }
  return value;
}

function stringAt(
  record: UnknownRecord,
  key: string,
  path: string,
  issues: MutableIssues,
  options: { optional?: boolean; max?: number } = {},
): string | null {
  const value = record[key];
  if (value == null && options.optional) return null;
  if (typeof value !== 'string') {
    issue(issues, `${path}.${key}`, options.optional ? 'type' : 'required', 'must be a string');
    return null;
  }
  if (value.trim().length === 0) {
    issue(issues, `${path}.${key}`, 'required', 'must not be empty');
  } else if (value.length > (options.max ?? 2_000)) {
    issue(issues, `${path}.${key}`, 'range', `must be at most ${options.max ?? 2_000} characters`);
  }
  return value;
}

function idAt(
  record: UnknownRecord,
  key: string,
  path: string,
  issues: MutableIssues,
  optional = false,
): string | null {
  const value = stringAt(record, key, path, issues, { optional, max: 200 });
  if (value != null && !ID_PATTERN.test(value)) {
    issue(issues, `${path}.${key}`, 'format', 'must be a stable ASCII id');
  }
  return value;
}

function enumAt<T extends string>(
  record: UnknownRecord,
  key: string,
  allowed: readonly T[],
  path: string,
  issues: MutableIssues,
  optional = false,
): T | null {
  const value = record[key];
  if (value == null && optional) return null;
  if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) {
    issue(issues, `${path}.${key}`, 'enum', `must be one of: ${allowed.join(', ')}`);
    return null;
  }
  return value as T;
}

function finiteNumberAt(
  record: UnknownRecord,
  key: string,
  path: string,
  issues: MutableIssues,
  options: { optional?: boolean; min?: number; max?: number } = {},
): number | null {
  const value = record[key];
  if (value == null && options.optional) return null;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    issue(issues, `${path}.${key}`, 'type', 'must be a finite number');
    return null;
  }
  if (options.min != null && value < options.min) {
    issue(issues, `${path}.${key}`, 'range', `must be at least ${options.min}`);
  }
  if (options.max != null && value > options.max) {
    issue(issues, `${path}.${key}`, 'range', `must be at most ${options.max}`);
  }
  return value;
}

function dateAt(
  record: UnknownRecord,
  key: string,
  path: string,
  issues: MutableIssues,
  optional = false,
): string | null {
  const value = record[key];
  if (value == null && optional) return null;
  if (typeof value !== 'string'
    || !(DATE_PATTERN.test(value) || ISO_INSTANT_PATTERN.test(value))
    || Number.isNaN(Date.parse(value))) {
    issue(issues, `${path}.${key}`, 'format', 'must be an ISO date or UTC instant');
    return null;
  }
  return value;
}

function shaAt(
  record: UnknownRecord,
  key: string,
  path: string,
  issues: MutableIssues,
  optional = false,
): string | null {
  const value = record[key];
  if (value == null && optional) return null;
  if (typeof value !== 'string' || !SHA256_PATTERN.test(value)) {
    issue(issues, `${path}.${key}`, 'format', 'must be a lowercase SHA-256 digest');
    return null;
  }
  return value;
}

function stringArrayAt(
  record: UnknownRecord,
  key: string,
  path: string,
  issues: MutableIssues,
  options: { min?: number; ids?: boolean; maxItems?: number } = {},
): string[] {
  const value = record[key];
  if (!Array.isArray(value)) {
    issue(issues, `${path}.${key}`, 'type', 'must be an array');
    return [];
  }
  if (value.length < (options.min ?? 0)) {
    issue(issues, `${path}.${key}`, 'required', `must contain at least ${options.min ?? 0} item(s)`);
  }
  if (value.length > (options.maxItems ?? 10_000)) {
    issue(issues, `${path}.${key}`, 'range', `must contain at most ${options.maxItems ?? 10_000} items`);
  }
  const strings: string[] = [];
  value.forEach((entry, index) => {
    if (typeof entry !== 'string' || entry.trim().length === 0 || entry.length > 2_000) {
      issue(issues, `${path}.${key}[${index}]`, 'type', 'must be a non-empty bounded string');
      return;
    }
    if (options.ids && !ID_PATTERN.test(entry)) {
      issue(issues, `${path}.${key}[${index}]`, 'format', 'must be a stable ASCII id');
    }
    strings.push(entry);
  });
  if (new Set(strings).size !== strings.length) {
    issue(issues, `${path}.${key}`, 'duplicate', 'must not contain duplicate values');
  }
  return strings;
}

function validateSchema(record: UnknownRecord, path: string, issues: MutableIssues): void {
  if (record.schemaVersion !== EQUIPMENT_LIBRARY_SCHEMA_VERSION) {
    issue(issues, `${path}.schemaVersion`, 'enum', `must equal ${EQUIPMENT_LIBRARY_SCHEMA_VERSION}`);
  }
}

function validateDigest(
  record: UnknownRecord,
  kind: EquipmentLibraryRecordKind,
  path: string,
  issues: MutableIssues,
): void {
  if (record.digestAlgorithm !== EQUIPMENT_LIBRARY_DIGEST_ALGORITHM) {
    issue(issues, `${path}.digestAlgorithm`, 'enum', `must equal ${EQUIPMENT_LIBRARY_DIGEST_ALGORITHM}`);
    return;
  }
  if (typeof record.contentDigest !== 'string' || !SHA256_PATTERN.test(record.contentDigest)) {
    issue(issues, `${path}.contentDigest`, 'format', 'must be a lowercase SHA-256 digest');
    return;
  }
  if (!verifyEquipmentLibraryRecordDigest(kind, record as unknown as EquipmentDigestFields)) {
    issue(issues, `${path}.contentDigest`, 'integrity', 'does not match the canonical record payload');
  }
}

function validateStringEnumArray(
  values: unknown,
  allowed: readonly string[],
  path: string,
  issues: MutableIssues,
): string[] {
  if (!Array.isArray(values)) {
    issue(issues, path, 'type', 'must be an array');
    return [];
  }
  const out: string[] = [];
  values.forEach((entry, index) => {
    if (typeof entry !== 'string' || !allowed.includes(entry)) {
      issue(issues, `${path}[${index}]`, 'enum', `must be one of: ${allowed.join(', ')}`);
      return;
    }
    out.push(entry);
  });
  if (new Set(out).size !== out.length) issue(issues, path, 'duplicate', 'must not contain duplicates');
  return out;
}

function validateObservedValue(
  value: unknown,
  path: string,
  issues: MutableIssues,
): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  const kind = enumAt(record, 'kind', ['scalar', 'range', 'curve', 'boolean', 'text'] as const, path, issues);
  if (kind === 'scalar') finiteNumberAt(record, 'value', path, issues);
  if (kind === 'range') {
    const low = finiteNumberAt(record, 'low', path, issues);
    const high = finiteNumberAt(record, 'high', path, issues);
    if (low != null && high != null && low > high) {
      issue(issues, path, 'range', 'range low must not exceed high');
    }
  }
  if (kind === 'curve') {
    stringAt(record, 'xUnit', path, issues, { max: 80 });
    stringAt(record, 'yUnit', path, issues, { max: 80 });
    if (!Array.isArray(record.points) || record.points.length < 2) {
      issue(issues, `${path}.points`, 'required', 'curve must contain at least two points');
    } else {
      let previousX = Number.NEGATIVE_INFINITY;
      record.points.forEach((point, index) => {
        const pointRecord = recordAt(point, `${path}.points[${index}]`, issues);
        if (!pointRecord) return;
        const x = finiteNumberAt(pointRecord, 'x', `${path}.points[${index}]`, issues);
        finiteNumberAt(pointRecord, 'y', `${path}.points[${index}]`, issues);
        if (x != null && x <= previousX) {
          issue(issues, `${path}.points[${index}].x`, 'invariant', 'curve x values must be strictly increasing');
        }
        if (x != null) previousX = x;
      });
    }
  }
  if (kind === 'boolean' && typeof record.value !== 'boolean') {
    issue(issues, `${path}.value`, 'type', 'must be a boolean');
  }
  if (kind === 'text') stringAt(record, 'value', path, issues, { max: 10_000 });
}

function validateObservationBasis(
  value: unknown,
  path: string,
  issues: MutableIssues,
): UnknownRecord | null {
  const basis = recordAt(value, path, issues);
  if (!basis) return null;
  enumAt(basis, 'qualifier', QUALIFIERS, path, issues);
  enumAt(basis, 'outputBoundary', OUTPUT_BOUNDARIES, path, issues, true);
  enumAt(basis, 'heatingValueBasis', HEATING_VALUE_BASES, path, issues, true);
  enumAt(basis, 'lifecycleBoundary', LIFECYCLE_BOUNDARIES, path, issues, true);
  if (basis.frequencyHz != null && basis.frequencyHz !== 50 && basis.frequencyHz !== 60) {
    issue(issues, `${path}.frequencyHz`, 'enum', 'must be 50 or 60 Hz');
  }
  if (basis.referenceOxygenPct != null) {
    finiteNumberAt(basis, 'referenceOxygenPct', path, issues, { min: 0, max: 100 });
  }
  [
    'duty',
    'fuel',
    'emissionsControl',
    'dryWetBasis',
    'grossNetBasis',
    'referenceConditions',
    'coolingMethod',
    'configuration',
    'notes',
  ].forEach(key => {
    if (basis[key] != null) stringAt(basis, key, path, issues, { optional: true, max: 2_000 });
  });
  return basis;
}

function validateMetric(value: unknown, path: string, issues: MutableIssues): string | null {
  if (typeof value !== 'string'
    || (!(EQUIPMENT_METRICS as readonly string[]).includes(value)
      && !/^custom:[A-Za-z0-9][A-Za-z0-9._:-]{0,119}$/.test(value))) {
    issue(issues, path, 'enum', 'must be a standard metric or a bounded custom:<id> metric');
    return null;
  }
  return value;
}

function validateProductInto(value: unknown, path: string, issues: MutableIssues): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  validateSchema(record, path, issues);
  idAt(record, 'id', path, issues);
  enumAt(record, 'level', EQUIPMENT_LEVELS, path, issues);
  enumAt(record, 'domain', EQUIPMENT_DOMAINS, path, issues);
  enumAt(record, 'kind', EQUIPMENT_KINDS, path, issues);
  stringAt(record, 'manufacturer', path, issues, { max: 300 });
  stringAt(record, 'family', path, issues, { optional: true, max: 300 });
  stringAt(record, 'model', path, issues, { max: 300 });
  stringAt(record, 'displayName', path, issues, { max: 500 });
  enumAt(record, 'lifecycleStatus', EQUIPMENT_LIFECYCLE_STATUSES, path, issues);
  stringArrayAt(record, 'tags', path, issues, { maxItems: 100 });
  idAt(record, 'currentRevisionId', path, issues, true);
  dateAt(record, 'introducedAt', path, issues, true);
  dateAt(record, 'retiredAt', path, issues, true);
  dateAt(record, 'createdAt', path, issues);
  dateAt(record, 'updatedAt', path, issues);

  const facets = recordAt(record.facets, `${path}.facets`, issues);
  if (facets) {
    validateStringEnumArray(facets.technologies, EQUIPMENT_TECHNOLOGIES, `${path}.facets.technologies`, issues);
    ['energySources', 'fuels', 'primeMovers', 'cycles', 'coolingMethods', 'gridRoles', 'applications', 'regions']
      .forEach(key => stringArrayAt(facets, key, `${path}.facets`, issues, { maxItems: 100 }));
  }

  const visibility = enumAt(
    record,
    'visibility',
    ['platform', 'public-registry', 'company-private'] as const,
    path,
    issues,
  );
  if (visibility === 'company-private') {
    if (record.origin !== 'company-private') {
      issue(issues, `${path}.origin`, 'invariant', 'private products must use company-private origin');
    }
    idAt(record, 'companyId', path, issues);
    idAt(record, 'createdByUid', path, issues);
  } else {
    enumAt(record, 'origin', [
      'oem-publication',
      'public-registry',
      'installed-fleet',
      'platform-curated',
      'planning-baseline',
    ] as const, path, issues);
    if ('companyId' in record || 'createdByUid' in record) {
      issue(issues, path, 'invariant', 'public/platform products must not carry tenant ownership');
    }
  }
  if (record.lifecycleStatus === 'retired' && record.retiredAt == null) {
    issue(issues, `${path}.retiredAt`, 'required', 'retired products require retiredAt');
  }
  validateDigest(record, 'product', path, issues);
}

function validateRevisionInto(value: unknown, path: string, issues: MutableIssues): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  validateSchema(record, path, issues);
  const id = idAt(record, 'id', path, issues);
  idAt(record, 'productId', path, issues);
  stringAt(record, 'revisionLabel', path, issues, { max: 300 });
  stringAt(record, 'variantLabel', path, issues, { max: 1_000 });
  const status = enumAt(record, 'status', EQUIPMENT_REVISION_STATUSES, path, issues);
  enumAt(record, 'configurationLevel', EQUIPMENT_LEVELS, path, issues);
  if (!Array.isArray(record.frequencyHz)) {
    issue(issues, `${path}.frequencyHz`, 'type', 'must be an array');
  } else {
    record.frequencyHz.forEach((frequency, index) => {
      if (frequency !== 50 && frequency !== 60) {
        issue(issues, `${path}.frequencyHz[${index}]`, 'enum', 'must be 50 or 60');
      }
    });
    if (new Set(record.frequencyHz).size !== record.frequencyHz.length) {
      issue(issues, `${path}.frequencyHz`, 'duplicate', 'must not contain duplicates');
    }
  }
  stringArrayAt(record, 'fuels', path, issues, { maxItems: 100 });
  stringArrayAt(record, 'duties', path, issues, { maxItems: 100 });
  const sources = stringArrayAt(record, 'sourceIds', path, issues, { ids: true, maxItems: 1_000 });
  const observations = stringArrayAt(record, 'observationIds', path, issues, { ids: true, maxItems: 10_000 });
  const supersedes = idAt(record, 'supersedesRevisionId', path, issues, true);
  if (id != null && supersedes === id) {
    issue(issues, `${path}.supersedesRevisionId`, 'invariant', 'a revision cannot supersede itself');
  }
  stringAt(record, 'manufacturerRevision', path, issues, { optional: true, max: 300 });
  dateAt(record, 'effectiveAt', path, issues, true);
  dateAt(record, 'publishedAt', path, issues, true);
  dateAt(record, 'compiledAt', path, issues);
  dateAt(record, 'reviewedAt', path, issues, true);
  stringAt(record, 'reviewedBy', path, issues, { optional: true, max: 300 });
  stringAt(record, 'notes', path, issues, { optional: true, max: 10_000 });
  if (status === 'reviewed') {
    if (sources.length === 0) issue(issues, `${path}.sourceIds`, 'required', 'reviewed revisions require a source');
    if (observations.length === 0) issue(issues, `${path}.observationIds`, 'required', 'reviewed revisions require observations');
    if (record.reviewedAt == null || record.reviewedBy == null) {
      issue(issues, path, 'required', 'reviewed revisions require reviewedAt and reviewedBy');
    }
  }
  validateDigest(record, 'revision', path, issues);
}

function validateSourceInto(value: unknown, path: string, issues: MutableIssues): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  validateSchema(record, path, issues);
  idAt(record, 'id', path, issues);
  enumAt(record, 'kind', EQUIPMENT_SOURCE_KINDS, path, issues);
  stringAt(record, 'publisher', path, issues, { max: 500 });
  stringAt(record, 'title', path, issues, { max: 1_000 });
  stringAt(record, 'revision', path, issues, { optional: true, max: 300 });
  dateAt(record, 'publishedAt', path, issues, true);
  dateAt(record, 'retrievedAt', path, issues);
  enumAt(record, 'access', EQUIPMENT_SOURCE_ACCESS, path, issues);
  shaAt(record, 'contentSha256', path, issues, true);
  stringAt(record, 'license', path, issues, { optional: true, max: 1_000 });
  stringAt(record, 'copyrightNotice', path, issues, { optional: true, max: 2_000 });
  stringAt(record, 'notes', path, issues, { optional: true, max: 10_000 });
  const visibility = enumAt(record, 'visibility', ['public', 'company-private'] as const, path, issues);
  if (visibility === 'public') {
    const url = stringAt(record, 'url', path, issues, { max: 4_000 });
    if (url != null && !url.startsWith('https://')) {
      issue(issues, `${path}.url`, 'format', 'public source URL must use HTTPS');
    }
    const archiveUrl = stringAt(record, 'archiveUrl', path, issues, { optional: true, max: 4_000 });
    if (archiveUrl != null && !archiveUrl.startsWith('https://')) {
      issue(issues, `${path}.archiveUrl`, 'format', 'archive URL must use HTTPS');
    }
    if (record.access === 'company-private') {
      issue(issues, `${path}.access`, 'invariant', 'public sources cannot use company-private access');
    }
  }
  if (visibility === 'company-private') {
    if (record.access !== 'company-private') {
      issue(issues, `${path}.access`, 'invariant', 'private sources must use company-private access');
    }
    idAt(record, 'companyId', path, issues);
    idAt(record, 'documentId', path, issues);
    stringAt(record, 'fileName', path, issues, { max: 1_000 });
    shaAt(record, 'contentSha256', path, issues);
    if ('url' in record || 'archiveUrl' in record) {
      issue(issues, path, 'invariant', 'private source records must not contain URLs');
    }
  }
  validateDigest(record, 'source', path, issues);
}

function validateObservationInto(value: unknown, path: string, issues: MutableIssues): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  validateSchema(record, path, issues);
  const id = idAt(record, 'id', path, issues);
  idAt(record, 'revisionId', path, issues);
  const metric = validateMetric(record.metric, `${path}.metric`, issues);
  validateObservedValue(record.value, `${path}.value`, issues);
  stringAt(record, 'unit', path, issues, { max: 120 });
  const basis = validateObservationBasis(record.basis, `${path}.basis`, issues);
  if (!Array.isArray(record.sourceRefs) || record.sourceRefs.length === 0) {
    issue(issues, `${path}.sourceRefs`, 'required', 'must contain at least one source reference');
  } else {
    const sourceIds: string[] = [];
    record.sourceRefs.forEach((sourceRef, index) => {
      const ref = recordAt(sourceRef, `${path}.sourceRefs[${index}]`, issues);
      if (!ref) return;
      const sourceId = idAt(ref, 'sourceId', `${path}.sourceRefs[${index}]`, issues);
      if (sourceId) sourceIds.push(sourceId);
      stringAt(ref, 'locator', `${path}.sourceRefs[${index}]`, issues, { optional: true, max: 1_000 });
      shaAt(ref, 'quoteDigest', `${path}.sourceRefs[${index}]`, issues, true);
    });
    if (new Set(sourceIds).size !== sourceIds.length) {
      issue(issues, `${path}.sourceRefs`, 'duplicate', 'must not repeat source ids');
    }
  }
  const evidenceStatus = enumAt(record, 'evidenceStatus', EQUIPMENT_EVIDENCE_STATUSES, path, issues);
  enumAt(record, 'confidence', ['low', 'medium', 'high'] as const, path, issues);
  dateAt(record, 'observedAt', path, issues, true);
  const validFrom = dateAt(record, 'validFrom', path, issues, true);
  const validTo = dateAt(record, 'validTo', path, issues, true);
  dateAt(record, 'reviewedAt', path, issues, true);
  stringAt(record, 'reviewedBy', path, issues, { optional: true, max: 300 });
  stringAt(record, 'notes', path, issues, { optional: true, max: 10_000 });
  if (validFrom && validTo && Date.parse(validFrom) > Date.parse(validTo)) {
    issue(issues, path, 'range', 'validFrom must not be after validTo');
  }
  if (metric === 'heat-rate' && basis?.heatingValueBasis == null) {
    issue(issues, `${path}.basis.heatingValueBasis`, 'required', 'heat rate requires an explicit LHV/HHV/not-stated basis');
  }
  if (metric === 'electrical-output' && basis?.outputBoundary == null) {
    issue(issues, `${path}.basis.outputBoundary`, 'required', 'electrical output requires an explicit output boundary');
  }
  if (metric != null && /^(co2|co2e|ch4|n2o|nox|so2|co|pm10|pm2\.5|voc|methane-slip)-emissions$/.test(metric)
    && basis?.lifecycleBoundary == null) {
    issue(issues, `${path}.basis.lifecycleBoundary`, 'required', 'emissions observations require a lifecycle boundary');
  }
  if (record.derivation != null) {
    const derivation = recordAt(record.derivation, `${path}.derivation`, issues);
    if (derivation) {
      stringAt(derivation, 'method', `${path}.derivation`, issues, { max: 2_000 });
      const inputs = stringArrayAt(derivation, 'inputObservationIds', `${path}.derivation`, issues, {
        ids: true,
        min: 1,
        maxItems: 100,
      });
      if (id != null && inputs.includes(id)) {
        issue(issues, `${path}.derivation.inputObservationIds`, 'invariant', 'an observation cannot derive from itself');
      }
    }
  } else if (evidenceStatus === 'derived-from-published') {
    issue(issues, `${path}.derivation`, 'required', 'derived observations require derivation metadata');
  }
  validateDigest(record, 'observation', path, issues);
}

function validateAdmissionInto(value: unknown, path: string, issues: MutableIssues): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  validateSchema(record, path, issues);
  idAt(record, 'id', path, issues);
  idAt(record, 'revisionId', path, issues);
  const surface = enumAt(record, 'surface', EQUIPMENT_ADMISSION_SURFACES, path, issues);
  const status = enumAt(record, 'status', EQUIPMENT_ADMISSION_STATUSES, path, issues);
  stringAt(record, 'adapterId', path, issues, { optional: true, max: 300 });
  stringAt(record, 'adapterVersion', path, issues, { optional: true, max: 300 });
  const required = stringArrayAt(record, 'requiredObservationIds', path, issues, {
    ids: true,
    maxItems: 10_000,
  });
  const missing = Array.isArray(record.missingMetrics) ? record.missingMetrics : [];
  if (!Array.isArray(record.missingMetrics)) {
    issue(issues, `${path}.missingMetrics`, 'type', 'must be an array');
  } else {
    record.missingMetrics.forEach((metric, index) => {
      validateMetric(metric, `${path}.missingMetrics[${index}]`, issues);
    });
  }
  stringArrayAt(record, 'conditions', path, issues, { maxItems: 100 });
  stringAt(record, 'reason', path, issues, { max: 5_000 });
  dateAt(record, 'reviewedAt', path, issues);
  stringAt(record, 'reviewedBy', path, issues, { max: 300 });
  idAt(record, 'supersedesAdmissionId', path, issues, true);
  const solverSurfaces: readonly EquipmentAdmissionSurface[] = [
    'generation-screening',
    'thermodynamic-solver',
    'electrical-study',
  ];
  if (status === 'admitted' && surface != null && solverSurfaces.includes(surface)) {
    if (record.adapterId == null || record.adapterVersion == null) {
      issue(issues, path, 'required', 'technical solver admissions require adapterId and adapterVersion');
    }
    if (required.length === 0) {
      issue(issues, `${path}.requiredObservationIds`, 'required', 'technical solver admissions require bound observations');
    }
  }
  if (status === 'needs-data' && missing.length === 0) {
    issue(issues, `${path}.missingMetrics`, 'required', 'needs-data admissions require missing metrics');
  }
  validateDigest(record, 'admission', path, issues);
}

function validateMarketEvidenceInto(value: unknown, path: string, issues: MutableIssues): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  validateSchema(record, path, issues);
  idAt(record, 'id', path, issues);
  idAt(record, 'productId', path, issues);
  idAt(record, 'revisionId', path, issues, true);
  idAt(record, 'supplierId', path, issues, true);
  stringAt(record, 'geography', path, issues, { max: 500 });
  stringArrayAt(record, 'sourceIds', path, issues, { ids: true, min: 1, maxItems: 100 });
  enumAt(record, 'evidenceStatus', EQUIPMENT_EVIDENCE_STATUSES, path, issues);
  enumAt(record, 'confidence', ['low', 'medium', 'high'] as const, path, issues);
  const validFrom = dateAt(record, 'validFrom', path, issues, true);
  const validTo = dateAt(record, 'validTo', path, issues, true);
  dateAt(record, 'observedAt', path, issues);
  stringAt(record, 'notes', path, issues, { optional: true, max: 10_000 });
  if (validFrom && validTo && Date.parse(validFrom) > Date.parse(validTo)) {
    issue(issues, path, 'range', 'validFrom must not be after validTo');
  }
  const kind = enumAt(record, 'evidenceKind', ['price', 'lead-time', 'production-slot', 'warranty'] as const, path, issues);
  if (kind === 'price') {
    finiteNumberAt(record, 'amount', path, issues, { min: 0 });
    const currency = stringAt(record, 'currency', path, issues, { max: 3 });
    if (currency != null && !/^[A-Z]{3}$/.test(currency)) {
      issue(issues, `${path}.currency`, 'format', 'must be an ISO 4217 currency code');
    }
    stringAt(record, 'priceBasis', path, issues, { max: 1_000 });
  }
  if (kind === 'lead-time') {
    const low = finiteNumberAt(record, 'lowMonths', path, issues, { min: 0, max: 600 });
    const high = finiteNumberAt(record, 'highMonths', path, issues, { min: 0, max: 600 });
    if (low != null && high != null && low > high) {
      issue(issues, path, 'range', 'lead-time lowMonths must not exceed highMonths');
    }
    stringAt(record, 'leadTimeBasis', path, issues, { max: 1_000 });
  }
  if (kind === 'production-slot') {
    enumAt(record, 'availability', ['available', 'limited', 'unavailable', 'reserved', 'unknown'] as const, path, issues);
    stringAt(record, 'deliveryWindow', path, issues, { optional: true, max: 1_000 });
  }
  if (kind === 'warranty') {
    finiteNumberAt(record, 'durationYears', path, issues, { min: 0, max: 200 });
    stringAt(record, 'coverage', path, issues, { max: 5_000 });
  }
  validateDigest(record, 'market-evidence', path, issues);
}

function validatePatchInto(value: unknown, path: string, issues: MutableIssues): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  validateSchema(record, path, issues);
  idAt(record, 'id', path, issues);
  idAt(record, 'companyId', path, issues);
  idAt(record, 'targetProductId', path, issues);
  shaAt(record, 'baseProductDigest', path, issues);
  const baseRevisionId = idAt(record, 'baseRevisionId', path, issues, true);
  const baseRevisionDigest = shaAt(record, 'baseRevisionDigest', path, issues, true);
  if ((baseRevisionId == null) !== (baseRevisionDigest == null)) {
    issue(issues, path, 'invariant', 'baseRevisionId and baseRevisionDigest must be supplied together');
  }
  const patch = recordAt(record.patch, `${path}.patch`, issues);
  if (patch) {
    const allowed = new Set([
      'displayName',
      'tags',
      'preferredSupplierIds',
      'privateObservationIds',
      'privateMarketEvidenceIds',
      'privateNotes',
      'active',
    ]);
    const unknown = Object.keys(patch).filter(key => !allowed.has(key));
    if (unknown.length > 0) {
      issue(issues, `${path}.patch`, 'invariant', `contains forbidden technical field(s): ${unknown.join(', ')}`);
    }
    if (Object.keys(patch).length === 0) {
      issue(issues, `${path}.patch`, 'required', 'must contain at least one patch field');
    }
    stringAt(patch, 'displayName', `${path}.patch`, issues, { optional: true, max: 500 });
    if (patch.tags != null) stringArrayAt(patch, 'tags', `${path}.patch`, issues, { maxItems: 100 });
    if (patch.preferredSupplierIds != null) stringArrayAt(patch, 'preferredSupplierIds', `${path}.patch`, issues, { ids: true, maxItems: 100 });
    if (patch.privateObservationIds != null) stringArrayAt(patch, 'privateObservationIds', `${path}.patch`, issues, { ids: true, maxItems: 10_000 });
    if (patch.privateMarketEvidenceIds != null) stringArrayAt(patch, 'privateMarketEvidenceIds', `${path}.patch`, issues, { ids: true, maxItems: 10_000 });
    stringAt(patch, 'privateNotes', `${path}.patch`, issues, { optional: true, max: 10_000 });
    if (patch.active != null && typeof patch.active !== 'boolean') {
      issue(issues, `${path}.patch.active`, 'type', 'must be a boolean');
    }
  }
  dateAt(record, 'createdAt', path, issues);
  dateAt(record, 'updatedAt', path, issues);
  idAt(record, 'updatedByUid', path, issues);
  validateDigest(record, 'company-patch', path, issues);
}

function validateLegacyInto(value: unknown, path: string, issues: MutableIssues): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  validateSchema(record, path, issues);
  idAt(record, 'id', path, issues);
  enumAt(record, 'namespace', LEGACY_EQUIPMENT_NAMESPACES, path, issues);
  stringAt(record, 'legacyId', path, issues, { max: 500 });
  idAt(record, 'productId', path, issues);
  idAt(record, 'revisionId', path, issues, true);
  enumAt(record, 'mapping', ['exact', 'family-alias', 'configuration-alias'] as const, path, issues);
  shaAt(record, 'frozenSnapshotDigest', path, issues, true);
  dateAt(record, 'createdAt', path, issues);
  stringAt(record, 'notes', path, issues, { optional: true, max: 10_000 });
  validateDigest(record, 'legacy-id', path, issues);
}

function validatePlanningMetricInto(
  value: unknown,
  path: string,
  issues: MutableIssues,
): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  idAt(record, 'id', path, issues);
  validateMetric(record.metric, `${path}.metric`, issues);
  validateObservedValue(record.value, `${path}.value`, issues);
  stringAt(record, 'unit', path, issues, { max: 120 });
  validateObservationBasis(record.basis, `${path}.basis`, issues);
  stringArrayAt(record, 'sourceIds', path, issues, { ids: true, min: 1, maxItems: 100 });
  enumAt(record, 'confidence', ['low', 'medium', 'high'] as const, path, issues);
  stringAt(record, 'notes', path, issues, { optional: true, max: 10_000 });
}

function validatePlanningBenchmarkInto(value: unknown, path: string, issues: MutableIssues): void {
  const record = recordAt(value, path, issues);
  if (!record) return;
  validateSchema(record, path, issues);
  idAt(record, 'id', path, issues);
  stringAt(record, 'name', path, issues, { max: 500 });
  enumAt(record, 'technology', EQUIPMENT_TECHNOLOGIES, path, issues);
  stringAt(record, 'geography', path, issues, { max: 500 });
  stringAt(record, 'scenario', path, issues, { max: 2_000 });
  const validFrom = dateAt(record, 'validFrom', path, issues, true);
  const validTo = dateAt(record, 'validTo', path, issues, true);
  stringArrayAt(record, 'sourceIds', path, issues, { ids: true, min: 1, maxItems: 100 });
  if (!Array.isArray(record.metrics) || record.metrics.length === 0) {
    issue(issues, `${path}.metrics`, 'required', 'must contain at least one planning metric');
  } else {
    record.metrics.forEach((metric, index) => validatePlanningMetricInto(metric, `${path}.metrics[${index}]`, issues));
    const ids = record.metrics
      .map(metric => isRecord(metric) && typeof metric.id === 'string' ? metric.id : null)
      .filter((id): id is string => id != null);
    if (new Set(ids).size !== ids.length) issue(issues, `${path}.metrics`, 'duplicate', 'metric ids must be unique');
  }
  dateAt(record, 'compiledAt', path, issues);
  dateAt(record, 'reviewedAt', path, issues, true);
  stringAt(record, 'reviewedBy', path, issues, { optional: true, max: 300 });
  if (validFrom && validTo && Date.parse(validFrom) > Date.parse(validTo)) {
    issue(issues, path, 'range', 'validFrom must not be after validTo');
  }
  validateDigest(record, 'planning-benchmark', path, issues);
}

export function validateEquipmentProduct(value: unknown): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  validateProductInto(value, 'product', issues);
  return result(issues);
}

export function validateEquipmentRevision(value: unknown): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  validateRevisionInto(value, 'revision', issues);
  return result(issues);
}

export function validateEquipmentSource(value: unknown): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  validateSourceInto(value, 'source', issues);
  return result(issues);
}

export function validateEquipmentObservation(value: unknown): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  validateObservationInto(value, 'observation', issues);
  return result(issues);
}

export function validateEquipmentAdmission(value: unknown): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  validateAdmissionInto(value, 'admission', issues);
  return result(issues);
}

export function validateEquipmentMarketEvidence(value: unknown): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  validateMarketEvidenceInto(value, 'marketEvidence', issues);
  return result(issues);
}

export function validateCompanyEquipmentPatch(value: unknown): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  validatePatchInto(value, 'companyPatch', issues);
  return result(issues);
}

export function validateLegacyEquipmentIdLink(value: unknown): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  validateLegacyInto(value, 'legacyId', issues);
  return result(issues);
}

export function validateEquipmentPlanningBenchmark(value: unknown): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  validatePlanningBenchmarkInto(value, 'planningBenchmark', issues);
  return result(issues);
}

function uniqueById(
  rows: readonly unknown[],
  path: string,
  issues: MutableIssues,
): Map<string, UnknownRecord> {
  const map = new Map<string, UnknownRecord>();
  rows.forEach((row, index) => {
    if (!isRecord(row) || typeof row.id !== 'string') return;
    if (map.has(row.id)) {
      issue(issues, `${path}[${index}].id`, 'duplicate', `duplicate id ${row.id}`);
    } else {
      map.set(row.id, row);
    }
  });
  return map;
}

function requireRef(
  map: ReadonlyMap<string, unknown>,
  id: unknown,
  path: string,
  label: string,
  issues: MutableIssues,
): UnknownRecord | null {
  if (typeof id !== 'string' || !map.has(id)) {
    issue(issues, path, 'reference', `${label} does not exist`);
    return null;
  }
  return map.get(id) as UnknownRecord;
}

export function validateEquipmentLibraryBundle(value: unknown): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  const bundle = recordAt(value, 'bundle', issues);
  if (!bundle) return result(issues);
  validateSchema(bundle, 'bundle', issues);
  dateAt(bundle, 'compiledAt', 'bundle', issues);

  const arrays = [
    'sources',
    'products',
    'revisions',
    'observations',
    'admissions',
    'marketEvidence',
    'companyPatches',
    'legacyIds',
    'planningBenchmarks',
  ] as const;
  arrays.forEach(key => {
    if (!Array.isArray(bundle[key])) issue(issues, `bundle.${key}`, 'type', 'must be an array');
  });
  if (issues.some(entry => entry.code === 'type' && entry.path.startsWith('bundle.'))) {
    validateDigest(bundle, 'bundle', 'bundle', issues);
    return result(issues);
  }

  const sources = bundle.sources as readonly EquipmentSource[];
  const products = bundle.products as readonly EquipmentLibraryProduct[];
  const revisions = bundle.revisions as readonly EquipmentRevision[];
  const observations = bundle.observations as readonly EquipmentObservation[];
  const admissions = bundle.admissions as readonly EquipmentAdmission[];
  const marketEvidence = bundle.marketEvidence as readonly EquipmentMarketEvidence[];
  const patches = bundle.companyPatches as readonly CompanyEquipmentPatch[];
  const legacyIds = bundle.legacyIds as readonly LegacyEquipmentIdLink[];
  const planningBenchmarks = bundle.planningBenchmarks as readonly EquipmentPlanningBenchmark[];

  sources.forEach((row, index) => validateSourceInto(row, `bundle.sources[${index}]`, issues));
  products.forEach((row, index) => validateProductInto(row, `bundle.products[${index}]`, issues));
  revisions.forEach((row, index) => validateRevisionInto(row, `bundle.revisions[${index}]`, issues));
  observations.forEach((row, index) => validateObservationInto(row, `bundle.observations[${index}]`, issues));
  admissions.forEach((row, index) => validateAdmissionInto(row, `bundle.admissions[${index}]`, issues));
  marketEvidence.forEach((row, index) => validateMarketEvidenceInto(row, `bundle.marketEvidence[${index}]`, issues));
  patches.forEach((row, index) => validatePatchInto(row, `bundle.companyPatches[${index}]`, issues));
  legacyIds.forEach((row, index) => validateLegacyInto(row, `bundle.legacyIds[${index}]`, issues));
  planningBenchmarks.forEach((row, index) => validatePlanningBenchmarkInto(row, `bundle.planningBenchmarks[${index}]`, issues));

  const sourceById = uniqueById(sources, 'bundle.sources', issues);
  const productById = uniqueById(products, 'bundle.products', issues);
  const revisionById = uniqueById(revisions, 'bundle.revisions', issues);
  const observationById = uniqueById(observations, 'bundle.observations', issues);
  uniqueById(admissions, 'bundle.admissions', issues);
  const marketById = uniqueById(marketEvidence, 'bundle.marketEvidence', issues);
  uniqueById(patches, 'bundle.companyPatches', issues);
  uniqueById(legacyIds, 'bundle.legacyIds', issues);
  uniqueById(planningBenchmarks, 'bundle.planningBenchmarks', issues);

  products.forEach((product, index) => {
    if (!product.currentRevisionId) return;
    const revision = requireRef(
      revisionById,
      product.currentRevisionId,
      `bundle.products[${index}].currentRevisionId`,
      'current revision',
      issues,
    );
    if (revision && revision.productId !== product.id) {
      issue(issues, `bundle.products[${index}].currentRevisionId`, 'reference', 'current revision belongs to another product');
    }
  });

  revisions.forEach((revision, index) => {
    requireRef(productById, revision.productId, `bundle.revisions[${index}].productId`, 'product', issues);
    revision.sourceIds.forEach((sourceId, sourceIndex) => {
      requireRef(sourceById, sourceId, `bundle.revisions[${index}].sourceIds[${sourceIndex}]`, 'source', issues);
    });
    revision.observationIds.forEach((observationId, observationIndex) => {
      const observation = requireRef(
        observationById,
        observationId,
        `bundle.revisions[${index}].observationIds[${observationIndex}]`,
        'observation',
        issues,
      );
      if (observation && observation.revisionId !== revision.id) {
        issue(issues, `bundle.revisions[${index}].observationIds[${observationIndex}]`, 'reference', 'observation belongs to another revision');
      }
    });
    if (revision.supersedesRevisionId) {
      const superseded = requireRef(
        revisionById,
        revision.supersedesRevisionId,
        `bundle.revisions[${index}].supersedesRevisionId`,
        'superseded revision',
        issues,
      );
      if (superseded && superseded.productId !== revision.productId) {
        issue(issues, `bundle.revisions[${index}].supersedesRevisionId`, 'reference', 'superseded revision belongs to another product');
      }
    }
  });

  observations.forEach((observation, index) => {
    requireRef(revisionById, observation.revisionId, `bundle.observations[${index}].revisionId`, 'revision', issues);
    observation.sourceRefs.forEach((sourceRef, sourceIndex) => {
      requireRef(sourceById, sourceRef.sourceId, `bundle.observations[${index}].sourceRefs[${sourceIndex}].sourceId`, 'source', issues);
    });
    observation.derivation?.inputObservationIds.forEach((inputId, inputIndex) => {
      requireRef(observationById, inputId, `bundle.observations[${index}].derivation.inputObservationIds[${inputIndex}]`, 'input observation', issues);
    });
  });

  admissions.forEach((admission, index) => {
    const revision = requireRef(
      revisionById,
      admission.revisionId,
      `bundle.admissions[${index}].revisionId`,
      'revision',
      issues,
    );
    admission.requiredObservationIds.forEach((observationId, observationIndex) => {
      const observation = requireRef(
        observationById,
        observationId,
        `bundle.admissions[${index}].requiredObservationIds[${observationIndex}]`,
        'required observation',
        issues,
      );
      if (revision && observation && observation.revisionId !== revision.id) {
        issue(issues, `bundle.admissions[${index}].requiredObservationIds[${observationIndex}]`, 'reference', 'required observation belongs to another revision');
      }
    });
  });

  marketEvidence.forEach((evidence, index) => {
    requireRef(productById, evidence.productId, `bundle.marketEvidence[${index}].productId`, 'product', issues);
    if (evidence.revisionId) {
      const revision = requireRef(
        revisionById,
        evidence.revisionId,
        `bundle.marketEvidence[${index}].revisionId`,
        'revision',
        issues,
      );
      if (revision && revision.productId !== evidence.productId) {
        issue(issues, `bundle.marketEvidence[${index}].revisionId`, 'reference', 'revision belongs to another product');
      }
    }
    evidence.sourceIds.forEach((sourceId, sourceIndex) => {
      requireRef(sourceById, sourceId, `bundle.marketEvidence[${index}].sourceIds[${sourceIndex}]`, 'source', issues);
    });
  });

  legacyIds.forEach((link, index) => {
    requireRef(productById, link.productId, `bundle.legacyIds[${index}].productId`, 'product', issues);
    if (link.revisionId) {
      const revision = requireRef(
        revisionById,
        link.revisionId,
        `bundle.legacyIds[${index}].revisionId`,
        'revision',
        issues,
      );
      if (revision && revision.productId !== link.productId) {
        issue(issues, `bundle.legacyIds[${index}].revisionId`, 'reference', 'revision belongs to another product');
      }
    }
  });

  planningBenchmarks.forEach((benchmark, index) => {
    benchmark.sourceIds.forEach((sourceId, sourceIndex) => {
      requireRef(sourceById, sourceId, `bundle.planningBenchmarks[${index}].sourceIds[${sourceIndex}]`, 'source', issues);
    });
    benchmark.metrics.forEach((metric: PlanningBenchmarkMetric, metricIndex) => {
      metric.sourceIds.forEach((sourceId, sourceIndex) => {
        requireRef(
          sourceById,
          sourceId,
          `bundle.planningBenchmarks[${index}].metrics[${metricIndex}].sourceIds[${sourceIndex}]`,
          'source',
          issues,
        );
      });
    });
  });

  patches.forEach((patch, index) => {
    const product = requireRef(
      productById,
      patch.targetProductId,
      `bundle.companyPatches[${index}].targetProductId`,
      'target product',
      issues,
    );
    if (product && product.contentDigest !== patch.baseProductDigest) {
      issue(issues, `bundle.companyPatches[${index}].baseProductDigest`, 'integrity', 'does not match target product');
    }
    if (patch.baseRevisionId) {
      const revision = requireRef(
        revisionById,
        patch.baseRevisionId,
        `bundle.companyPatches[${index}].baseRevisionId`,
        'base revision',
        issues,
      );
      if (revision && revision.contentDigest !== patch.baseRevisionDigest) {
        issue(issues, `bundle.companyPatches[${index}].baseRevisionDigest`, 'integrity', 'does not match base revision');
      }
    }
    patch.patch.privateObservationIds?.forEach((observationId, observationIndex) => {
      const observation = requireRef(
        observationById,
        observationId,
        `bundle.companyPatches[${index}].patch.privateObservationIds[${observationIndex}]`,
        'private observation',
        issues,
      );
      if (!observation) return;
      const revision = typeof observation.revisionId === 'string'
        ? revisionById.get(observation.revisionId)
        : undefined;
      if (revision?.productId !== patch.targetProductId) {
        issue(issues, `bundle.companyPatches[${index}].patch.privateObservationIds[${observationIndex}]`, 'reference', 'private observation belongs to another product');
      }
    });
    patch.patch.privateMarketEvidenceIds?.forEach((evidenceId, evidenceIndex) => {
      const evidence = requireRef(
        marketById,
        evidenceId,
        `bundle.companyPatches[${index}].patch.privateMarketEvidenceIds[${evidenceIndex}]`,
        'private market evidence',
        issues,
      );
      if (evidence && evidence.productId !== patch.targetProductId) {
        issue(issues, `bundle.companyPatches[${index}].patch.privateMarketEvidenceIds[${evidenceIndex}]`, 'reference', 'private market evidence belongs to another product');
      }
    });
  });

  validateDigest(bundle, 'bundle', 'bundle', issues);
  return result(issues);
}

export function assertValidEquipmentLibraryBundle(
  value: unknown,
): asserts value is EquipmentLibraryBundle {
  const validation = validateEquipmentLibraryBundle(value);
  if (!validation.valid) {
    throw new TypeError(
      `Invalid equipment library bundle: ${validation.issues
        .map(entry => `${entry.path}: ${entry.message}`)
        .join('; ')}`,
    );
  }
}

/** Exported for focused validation of benchmark values without product records. */
export function validatePlanningBenchmarkMetric(
  value: unknown,
): EquipmentLibraryValidationResult {
  const issues: MutableIssues = [];
  validatePlanningMetricInto(value, 'planningMetric', issues);
  return result(issues);
}

/** Type-only compatibility helpers for consumers constructing fixtures. */
export type {
  EquipmentAdmission,
  EquipmentLibraryProduct,
  EquipmentMarketEvidence,
  EquipmentObservation,
  EquipmentObservedValue,
  EquipmentPlanningBenchmark,
  EquipmentRevision,
  EquipmentSource,
  LegacyEquipmentIdLink,
};
